import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
export const EASE = [0.33, 1, 0.68, 1] as const;

/* ------------------------------------------------------------------ */
/* Eyebrow                                                             */
/* ------------------------------------------------------------------ */
export function Eyebrow({
  children,
  tone = 'dark',
  centered = false
}: {
  children: ReactNode;
  tone?: 'dark' | 'light';
  centered?: boolean;
}) {
  const color = tone === 'dark' ? 'text-espresso/45' : 'text-pearl/60';
  return <div className={`inline-flex items-center gap-3 ${centered ? 'justify-center' : ''}`} data-id="element-180" data-mp="1ziz7wa9gbu8p">
      <span className="h-px w-8 bg-bronze" aria-hidden="true" data-id="element-181" data-mp="24ilmoo4xhijm" />
      <span className={`text-[10px] font-semibold uppercase tracking-[0.25em] ${color}`} data-id="element-182" data-mp="24k6jkkeptdkn">
        {children}
      </span>
    </div>;
}

/* ------------------------------------------------------------------ */
/* Section heading                                                     */
/* ------------------------------------------------------------------ */
export function SectionHeading({
  eyebrow,
  title,
  sub,
  tone = 'dark',
  align = 'left',
  className = ''
}: {
  eyebrow?: string;
  title: string;
  sub?: string;
  tone?: 'dark' | 'light';
  align?: 'left' | 'center';
  className?: string;
}) {
  const titleColor = tone === 'dark' ? 'text-espresso' : 'text-pearl';
  const subColor = tone === 'dark' ? 'text-espresso/60' : 'text-pearl/65';
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.7,
    ease: EASE
  }} className={`${align === 'center' ? 'text-center mx-auto max-w-2xl' : 'max-w-2xl'} ${className}`} data-id="element-183" data-mp="3jdaopiuz12dn">
      {eyebrow && <div className={align === 'center' ? 'flex justify-center' : ''} data-id="element-184" data-mp="660lwg36t088">
          <Eyebrow tone={tone} data-id="element-185" data-mp="64pg6aq6lieh">{eyebrow}</Eyebrow>
        </div>}
      <h2 className={`mt-5 font-serif text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-[2.75rem] ${titleColor}`} data-id="element-186" data-mp="20ew41x89pwtd">
        {title}
      </h2>
      {sub && <p className={`mt-4 text-base font-light leading-relaxed ${subColor}`} data-id="element-187" data-mp="1qn038eesrslg">
          {sub}
        </p>}
    </motion.div>;
}

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */
type ButtonVariant = 'solid' | 'outline' | 'light';
const buttonBase = 'inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze focus-visible:ring-offset-2';
const buttonVariants: Record<ButtonVariant, string> = {
  solid: 'bg-espresso text-cream hover:bg-ink',
  outline: 'border border-espresso/25 text-espresso hover:border-espresso hover:bg-espresso hover:text-cream',
  light: 'border border-pearl/30 text-pearl hover:bg-pearl hover:text-espresso'
};
export function ButtonLink({
  to,
  variant = 'solid',
  children,
  className = ''
}: {
  to: string;
  variant?: ButtonVariant;
  children: ReactNode;
  className?: string;
}) {
  return <Link to={to} className={`${buttonBase} ${buttonVariants[variant]} ${className}`} data-id="element-188" data-mp="1e4wani4ew3br">
      {children}
    </Link>;
}
export function ButtonAnchor({
  href,
  variant = 'solid',
  children,
  className = ''
}: {
  href: string;
  variant?: ButtonVariant;
  children: ReactNode;
  className?: string;
}) {
  return <a href={href} className={`${buttonBase} ${buttonVariants[variant]} ${className}`} data-id="element-189" data-mp="1j5tqvp5v2nam">
      {children}
    </a>;
}
export function Button({
  onClick,
  type = 'button',
  variant = 'solid',
  children,
  className = '',
  disabled
}: {
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: ButtonVariant;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  return <button type={type} onClick={onClick} disabled={disabled} className={`${buttonBase} ${buttonVariants[variant]} disabled:cursor-not-allowed disabled:opacity-40 ${className}`} data-id="element-190" data-mp="3raatd068dfhu">
      {children}
    </button>;
}

/* ------------------------------------------------------------------ */
/* Quiet inline link with arrow                                        */
/* ------------------------------------------------------------------ */
export function ArrowLink({
  to,
  children,
  tone = 'dark'
}: {
  to: string;
  children: ReactNode;
  tone?: 'dark' | 'light';
}) {
  const color = tone === 'dark' ? 'text-espresso/70 hover:text-espresso' : 'text-pearl/70 hover:text-pearl';
  return <Link to={to} className={`group inline-flex items-center gap-2 border-b border-bronze/50 pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors ${color}`} data-id="element-191" data-mp="2puwitr76tjv5">
      {children}
      <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1" data-id="element-192" data-mp="12xwxgm70wwmb">
        →
      </span>
    </Link>;
}

/* ------------------------------------------------------------------ */
/* Section wrapper                                                     */
/* ------------------------------------------------------------------ */
export function Section({
  id,
  children,
  tone = 'cream',
  className = ''
}: {
  id?: string;
  children: ReactNode;
  tone?: 'cream' | 'white' | 'espresso' | 'sand';
  className?: string;
}) {
  const tones = {
    cream: 'bg-cream',
    white: 'bg-white',
    sand: 'bg-sand/40',
    espresso: 'bg-espresso text-pearl'
  };
  return <section id={id} className={`relative px-5 py-20 sm:px-8 lg:px-10 lg:py-28 ${tones[tone]} ${className}`} data-id="element-193" data-mp="59d70o1uc9rm">
      {tone === 'espresso' && <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-grain opacity-60" data-id="element-194" data-mp="3mtn5tlrf8af8" />}
      <div className="relative z-10 mx-auto w-full max-w-6xl" data-id="element-195" data-mp="3rt9jfwb5vsok">{children}</div>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Reveal wrapper                                                      */
/* ------------------------------------------------------------------ */
export function Reveal({
  children,
  delay = 0,
  className = ''
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.7,
    delay,
    ease: EASE
  }} className={className} data-id="element-196" data-mp="mov4kchqyv8">
      {children}
    </motion.div>;
}

/* ------------------------------------------------------------------ */
/* Hairline rule                                                       */
/* ------------------------------------------------------------------ */
export function Rule({
  tone = 'dark'
}: {
  tone?: 'dark' | 'light';
}) {
  return <div aria-hidden="true" className={`h-px w-full ${tone === 'dark' ? 'bg-espresso/10' : 'bg-pearl/15'}`} data-id="element-197" data-mp="2rka108fvria9" />;
}